import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Building2,
  X,
  List,
  Gift,
} from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useTranslation } from "react-i18next";

interface SubItem {
  title: string;
  url: string;
  icon: React.ElementType;
  match?: string;
  perm?: string;
}

interface NavItem {
  perm?: string;
  title: string;
  url?: string;
  icon: React.ElementType;
  children?: SubItem[];
  match?: string;
}

export const getNavItems = (t: (key: string) => string): NavItem[] => [
  { title: t("nav.home"), url: "/home", icon: Home },
  { title: t("nav.loan_statement"), url: "/loan-statement", icon: List },
   { title: t("nav.spin_win"), url: "/spin-win", icon: Gift },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export function Sidebar({
  collapsed,
  onToggle,
  mobileOpen,
  onMobileClose,
}: SidebarProps) {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();

  const navItems = useMemo(() => getNavItems(t), [t]);

  const dir =
    (document.documentElement.getAttribute("dir") as "ltr" | "rtl") ||
    i18n.dir() ||
    "ltr";

  const isRTL = dir === "rtl";
  const isDark = theme === "dark";

  const [openGroups, setOpenGroups] = useState<string[]>([]);

  const normalize = (path: string) => path.replace(/\/+$/, "") || "/";

  const isChildActive = (child: SubItem) => {
    const current = normalize(location.pathname);
    const matchPath = normalize(child.match || child.url);
    return current === matchPath || current.startsWith(matchPath + "/");
  };

  const isNavActive = (item: NavItem) => {
    if (!item.url) return false;
    const current = normalize(location.pathname);
    const matchPath = normalize(item.match || item.url);
    return current === matchPath || current.startsWith(matchPath + "/");
  };

  const isGroupActive = (children?: SubItem[]) =>
    children?.some(isChildActive) ?? false;

  useEffect(() => {
    const activeGroup = navItems.find(
      (item) => item.children && isGroupActive(item.children)
    );

    setOpenGroups(activeGroup ? [activeGroup.title] : []);
  }, [location.pathname, navItems]);

  const toggleGroup = (title: string) => {
    setOpenGroups((prev) =>
      prev.includes(title)
        ? prev.filter((item) => item !== title)
        : [...prev, title]
    );
  };

  const sidebarBase = isDark
    ? "bg-gradient-to-b from-[#1b1930] to-[#0b0a14] border-white/10 text-white"
    : "bg-gradient-to-b from-slate-50 to-slate-100 border-slate-200 text-slate-900";

  const logoText = isDark ? "text-white" : "text-slate-900";
  const mutedText = isDark
    ? "text-white/60 hover:text-white"
    : "text-slate-600 hover:text-slate-900";

  const itemBase = isDark
    ? "text-white hover:bg-white/10"
    : "text-slate-800 hover:bg-slate-200/60";

  const groupInactive = itemBase;
  const groupActive = isDark
    ? "bg-white/10 text-white"
    : "bg-slate-200 text-slate-900";

  const childInactive = isDark
    ? "text-white/70 hover:bg-white/10 hover:text-white"
    : "text-slate-600 hover:bg-slate-200/60 hover:text-slate-900";

  const primaryActive = isDark
    ? "bg-[#7C3AED] text-white shadow-[0_0_0_1px_rgba(124,58,237,0.35),0_8px_30px_rgba(124,58,237,0.18)]"
    : "bg-[#7C3AED] text-white shadow-[0_0_0_1px_rgba(124,58,237,0.25),0_10px_30px_rgba(124,58,237,0.12)]";

  const padX = isRTL ? "pr-3 pl-2" : "pl-3 pr-2";
  const childIndent = isRTL ? "mr-4" : "ml-4";
  const accentSide = isRTL ? "right-0" : "left-0";
  const logoGap = isRTL ? "mr-3" : "ml-3";

  const sidebarClasses = `
    fixed top-0 h-screen z-40 flex flex-col transition-all duration-300
    ${isRTL ? "right-0 border-l border-r-0" : "left-0 border-r"}
    ${sidebarBase}
    ${collapsed ? "lg:w-16 w-64" : "w-64"}
    ${mobileOpen ? "translate-x-0" : isRTL ? "translate-x-full" : "-translate-x-full"}
    lg:translate-x-0
  `;

  return (
    <>
      <AnimatePresence>
        {mobileOpen && (
          <motion.button
            type="button"
            aria-label="Close sidebar"
            onClick={onMobileClose}
            className="fixed inset-0 z-30 bg-black/40 lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
        )}
      </AnimatePresence>

      <aside className={sidebarClasses} dir={dir}>
        <div
          className={`flex items-center justify-between h-16 px-4 border-b ${
            isDark ? "border-white/10" : "border-slate-200"
          }`}
        >
          <div className="flex items-center min-w-0">
            <Building2
              className={`h-7 w-7 shrink-0 ${
                isDark ? "text-[#A78BFA]" : "text-[#7C3AED]"
              }`}
            />
            {!collapsed && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={`${logoGap} text-lg font-bold truncate ${logoText}`}
              >
                LoanPro
              </motion.span>
            )}
          </div>

          <button
            type="button"
            onClick={onMobileClose}
            className={`lg:hidden ${mutedText}`}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-1">
          {navItems.map((item) => {
            if (item.children) {
              const groupOpen = openGroups.includes(item.title);
              const groupActiveNow = isGroupActive(item.children);

              return (
                <div key={item.title} className="space-y-1">
                  <button
                    type="button"
                    onClick={() => (collapsed ? onToggle() : toggleGroup(item.title))}
                    className={`relative w-full flex items-center gap-3 ${padX} py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                      groupActiveNow ? groupActive : groupInactive
                    }`}
                  >
                    {groupActiveNow && (
                      <span
                        className={`absolute ${accentSide} top-2 bottom-2 w-1 rounded-full bg-[#A78BFA]`}
                      />
                    )}

                    <item.icon className="h-5 w-5 shrink-0" />

                    {!collapsed && (
                      <>
                        <span className="uppercase flex-1 text-start truncate">
                          {item.title}
                        </span>
                        <motion.span
                          animate={{ rotate: groupOpen ? 180 : 0 }}
                          transition={{ duration: 0.18 }}
                        >
                          <ChevronDown className="h-4 w-4" />
                        </motion.span>
                      </>
                    )}
                  </button>

                  <AnimatePresence initial={false}>
                    {groupOpen && !collapsed && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className={`overflow-hidden ${childIndent} mt-1 space-y-0.5`}
                      >
                        {item.children.map((child) => (
                          <NavLink
                            key={child.url}
                            to={child.url}
                            onClick={onMobileClose}
                            className={`relative flex items-center gap-3 ${padX} py-2 rounded-xl text-sm transition-colors ${
                              isChildActive(child) ? primaryActive : childInactive
                            }`}
                          >
                            {isChildActive(child) && (
                              <span
                                className={`absolute ${accentSide} top-2 bottom-2 w-1 rounded-full bg-white/70`}
                              />
                            )}
                            <child.icon className="h-4 w-4 shrink-0" />
                            <span className="uppercase truncate">{child.title}</span>
                          </NavLink>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            }

            return (
              <NavLink
                key={item.title}
                to={item.url!}
                onClick={onMobileClose}
                className={`relative flex items-center gap-3 ${padX} py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                  isNavActive(item) ? primaryActive : itemBase
                }`}
              >
                {isNavActive(item) && (
                  <span
                    className={`absolute ${accentSide} top-2 bottom-2 w-1 rounded-full bg-white/70`}
                  />
                )}
                <item.icon className="h-5 w-5 shrink-0" />
                {!collapsed && <span className="uppercase truncate">{item.title}</span>}
              </NavLink>
            );
          })}
        </nav>

        <button
          type="button"
          onClick={onToggle}
          className={`hidden lg:flex items-center justify-center h-12 border-t transition-colors ${
            isDark
              ? "border-white/10 text-white/60 hover:text-white"
              : "border-slate-200 text-slate-600 hover:text-slate-900"
          }`}
          title={collapsed ? "Expand" : "Collapse"}
        >
          {collapsed ? (
            isRTL ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />
          ) : (
            isRTL ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />
          )}
        </button>
      </aside>
    </>
  );
}